import React from "react";

import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";

import { HERO_CHART_HEIGHT } from "../constants";
import { InsightsSkeletonRows } from "./InsightsSkeletonRows";
import {
  ChartArea,
  Footnote,
  Headline,
  HeroCard,
  SubStat,
  SubStats,
  TopBar,
} from "./SavingsHero/styles";
import {
  FiltersCount,
  FiltersGroup,
  FiltersSkeleton,
  HoldingsCard,
  HoldingsCardRow,
  HoldingsCards,
  HoldingsNameCell,
  HoldingsSkeleton,
  HoldingsTable,
  HoldingsTableFoot,
  HoldingsTableHead,
  HoldingsTableRow,
  HoldingsTrendCell,
  HoldingsValueCell,
  InsightsBody,
  InsightsColumn,
  InsightsColumnHeader,
  InsightsHeader,
  InsightsHeaderSpacer,
  InsightsSkeleton,
  SkeletonBox,
  SkeletonWrapper,
} from "./styles";

// Placeholder counts, sized to the real layout: a 7-column holdings table with
// five body rows, two insight columns of descending bars, and three mobile
// cards. Fewer rows than a full page keeps the shimmer honest without implying
// an exact item count.
const TABLE_ROWS = [0, 1, 2, 3, 4];
const VALUE_CELLS = [0, 1, 2, 3, 4];
const INSIGHT_COLUMNS = ["Loading allocation", "Loading gross interest"];
const MOBILE_CARDS = [0, 1, 2];

/**
 * Skeleton loading state for SavingsPage.
 *
 * Mirrors the loaded composition (Hero, Filters, Insights, Holdings) so the
 * page keeps its shape while data is fetched, avoiding cumulative layout shift.
 */
export const SavingsSkeleton: React.FC = () => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const heroChartHeight = isCompact
    ? `${HERO_CHART_HEIGHT.compact}px`
    : `${HERO_CHART_HEIGHT.regular}px`;

  return (
    <SkeletonWrapper
      aria-label="Loading savings"
      aria-live="polite"
      aria-busy="true"
    >
      {/* Hero: period label + Create button, big current value, line chart,
          Deposited/Withdrawn substats, net-contributed footnote. Reuses the
          real hero's styled shell so the skeleton can't drift from the page. */}
      <HeroCard>
        <TopBar>
          <SkeletonBox width="160px" height="14px" />
          {isCompact ? null : <SkeletonBox width="170px" height="40px" />}
        </TopBar>
        <Headline>
          <SkeletonBox width="240px" height="48px" />
          <SkeletonBox width="150px" height="22px" />
        </Headline>
        <ChartArea>
          <SkeletonBox height={heroChartHeight} />
        </ChartArea>
        <SubStats>
          <SubStat>
            <SkeletonBox width="80px" height="16px" />
            <SkeletonBox width="90px" height="16px" />
          </SubStat>
          <SubStat>
            <SkeletonBox width="80px" height="16px" />
            <SkeletonBox width="90px" height="16px" />
          </SubStat>
        </SubStats>
        <Footnote>
          <SkeletonBox width="260px" height="14px" />
        </Footnote>
      </HeroCard>

      {/* Filters: period select + active-only toggle, with an inline count on
          desktop where the filter row renders as a pill. */}
      <FiltersSkeleton>
        <FiltersGroup>
          <SkeletonBox width="140px" height="40px" />
          <SkeletonBox width="110px" height="24px" />
        </FiltersGroup>
        <FiltersCount>
          <SkeletonBox width="120px" height="18px" />
        </FiltersCount>
      </FiltersSkeleton>

      {/* Insights: collapsible header, then Allocation + Gross interest
          columns (revealed at tablet-large and up, matching the live default). */}
      <InsightsSkeleton>
        <InsightsHeader>
          <SkeletonBox width="20px" height="20px" />
          <SkeletonBox width="90px" height="18px" />
          <SkeletonBox width="180px" height="14px" />
          <InsightsHeaderSpacer />
          <SkeletonBox width="60px" height="14px" />
        </InsightsHeader>
        <InsightsBody>
          {INSIGHT_COLUMNS.map((column) => (
            <InsightsColumn key={column}>
              <InsightsColumnHeader>
                <SkeletonBox width="120px" height="18px" />
                <SkeletonBox width="200px" height="13px" />
              </InsightsColumnHeader>
              <InsightsSkeletonRows label={column} />
            </InsightsColumn>
          ))}
        </InsightsBody>
      </InsightsSkeleton>

      {/* Holdings: table on desktop, card list on compact. */}
      <HoldingsSkeleton>
        <HoldingsTable>
          <HoldingsTableHead>
            <HoldingsNameCell>
              <SkeletonBox width="80px" height="12px" />
            </HoldingsNameCell>
            {VALUE_CELLS.map((cell) => (
              <HoldingsValueCell key={cell}>
                <SkeletonBox width="60px" height="12px" />
              </HoldingsValueCell>
            ))}
            <HoldingsTrendCell>
              <SkeletonBox width="50px" height="12px" />
            </HoldingsTrendCell>
          </HoldingsTableHead>
          {TABLE_ROWS.map((row) => (
            <HoldingsTableRow key={row}>
              <HoldingsNameCell>
                <SkeletonBox width="140px" height="18px" />
                <SkeletonBox width="90px" height="14px" />
              </HoldingsNameCell>
              {VALUE_CELLS.map((cell) => (
                <HoldingsValueCell key={cell}>
                  <SkeletonBox width="72px" height="16px" />
                </HoldingsValueCell>
              ))}
              <HoldingsTrendCell>
                <SkeletonBox width="120px" height="36px" />
              </HoldingsTrendCell>
            </HoldingsTableRow>
          ))}
          <HoldingsTableFoot>
            <SkeletonBox width="200px" height="14px" />
          </HoldingsTableFoot>
        </HoldingsTable>

        <HoldingsCards>
          {MOBILE_CARDS.map((card) => (
            <HoldingsCard key={card}>
              <SkeletonBox width="150px" height="18px" />
              <HoldingsCardRow>
                <SkeletonBox width="120px" height="14px" />
              </HoldingsCardRow>
              <HoldingsCardRow>
                <SkeletonBox width="140px" height="28px" />
                <SkeletonBox width="80px" height="18px" />
              </HoldingsCardRow>
              <SkeletonBox height="64px" />
              <SkeletonBox width="90px" height="13px" />
            </HoldingsCard>
          ))}
        </HoldingsCards>
      </HoldingsSkeleton>
    </SkeletonWrapper>
  );
};
